import React, { useState, useEffect } from "react";
import { useAction } from "wasp/client/operations";
import { createCarpool } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { useNavigate } from "react-router-dom";
import {
  IndividualEditPageLayout,
  SidebarSection,
  ActionButtons,
  ActionButton,
  ActionButtonSecondary,
} from "../../components/layout";
import { useLocationContext } from "../../client/contexts/LocationContext";
import {
  Car,
  MapPin,
  Calendar,
  Clock,
  Users,
  DollarSign,
  Phone,
  Mail,
  Save,
  AlertCircle,
} from "lucide-react";

interface CarpoolFormData {
  role: "DRIVER" | "PASSENGER";
  origin: string;
  destination: string;
  date: string;
  time: string;
  isRecurring: boolean;
  daysOfWeek: string[];
  seats: string;
  cost: string;
  vehicle: string;
  description: string;
  contactPhone: string;
  contactEmail: string;
}

const WEEKDAYS = [
  { value: "MON", label: "M" },
  { value: "TUE", label: "T" },
  { value: "WED", label: "W" },
  { value: "THU", label: "T" },
  { value: "FRI", label: "F" },
  { value: "SAT", label: "S" },
  { value: "SUN", label: "S" },
];

export default function AddCarpoolPage() {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const createCarpoolFn = useAction(createCarpool);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const [formData, setFormData] = useState<CarpoolFormData>({
    role: "DRIVER",
    origin: "",
    destination: "",
    date: new Date().toISOString().split("T")[0],
    time: "",
    isRecurring: false,
    daysOfWeek: [],
    seats: "1",
    cost: "",
    vehicle: "",
    description: "",
    contactPhone: user?.phoneNumber || "",
    contactEmail: user?.email || "",
  });

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        contactPhone: prev.contactPhone || user.phoneNumber || "",
        contactEmail: prev.contactEmail || user.email || "",
      }));
    }
  }, [user]);

  const formatPhoneNumber = (value: string) => {
    const numbers = value?.replace(/\D/g, "");
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handleChange = (field: keyof CarpoolFormData, value: any) => {
    if (field === "contactPhone") {
      value = formatPhoneNumber(value);
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const toggleDay = (day: string) => {
    setFormData(prev => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter(d => d !== day)
        : [...prev.daysOfWeek, day]
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.origin.trim()) newErrors.origin = "Starting point is required";
    if (!formData.destination.trim()) newErrors.destination = "Destination is required";
    if (!formData.date) newErrors.date = "Date is required";
    if (!formData.time) newErrors.time = "Time is required";
    if (!selectedLocation?.id) newErrors.location = "No location selected";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      await createCarpoolFn({
        locationId: selectedLocation!.id,
        role: formData.role,
        origin: formData.origin.trim(),
        destination: formData.destination.trim(),
        date: new Date(formData.date).toISOString(),
        time: formData.time,
        isRecurring: formData.isRecurring,
        daysOfWeek: formData.daysOfWeek,
        seats: parseInt(formData.seats) || 1,
        cost: formData.cost ? parseFloat(formData.cost) : 0,
        vehicle: formData.vehicle.trim(),
        description: formData.description.trim(),
        email: formData.contactEmail,
        phoneNumber: formData.contactPhone,
        visibility: "VERIFIED",
      });
      handleGoBack();
    } catch (error) {
      console.error("Error creating carpool:", error);
      setErrors({ submit: "Failed to post carpool." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoBack = () => {
    if (selectedLocation?.locationCode) {
      navigate(`/locations/${selectedLocation.locationCode}/carpooling`);
    } else {
      navigate("/carpooling");
    }
  };

  if (!user) {
    return (
      <IndividualEditPageLayout title="Access Denied" icon={AlertCircle}>
        <div className="p-6 text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Access Denied
          </h3>
          <p className="text-gray-600">You must be logged in to post carpools.</p>
        </div>
      </IndividualEditPageLayout>
    );
  }

  const actionsContent = (
    <ActionButtons>
      <ActionButton
        type="submit"
        form="carpool-form"
        disabled={isSubmitting}
        icon={Save}
      >
        {isSubmitting ? "Posting..." : "Post Carpool"}
      </ActionButton>
      <ActionButtonSecondary onClick={handleGoBack}>
        Cancel
      </ActionButtonSecondary>
    </ActionButtons>
  );

  const sidebarContent = (
    <SidebarSection title="Ride Type" hideOnMobile>
        <div className="flex bg-slate-100 p-1 rounded-xl w-full">
          <button
            type="button"
            onClick={() => handleChange("role", "DRIVER")}
            className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${
              formData.role === "DRIVER" ? "bg-blue-600 text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            Driver
          </button>
          <button
            type="button"
            onClick={() => handleChange("role", "PASSENGER")}
            className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${
              formData.role === "PASSENGER" ? "bg-blue-600 text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            Passenger
          </button>
        </div>
    </SidebarSection>
  );

  return (
    <IndividualEditPageLayout
      title="Post Carpool"
      subtitle="Share a ride with classmates"
      icon={Car}
      sidebar={sidebarContent}
      actions={actionsContent}
    >
      <form id="carpool-form" onSubmit={handleSubmit} className="space-y-8">
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-slate-900 mb-2 uppercase tracking-tight">Starting From</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={formData.origin}
                  onChange={(e) => handleChange("origin", e.target.value)}
                  placeholder="e.g., Campus Center"
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-900 mb-2 uppercase tracking-tight">Going To</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={formData.destination}
                  onChange={(e) => handleChange("destination", e.target.value)}
                  placeholder="e.g., Downtown Station"
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2 uppercase tracking-tight">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={3}
              placeholder="e.g., Driving a silver Honda Civic. Can drop off anywhere along Main St."
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-6">
            <h3 className="font-bold text-slate-900 flex items-center gap-2 px-1"><Calendar className="w-4 h-4 text-blue-600" /> Schedule</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Date</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleChange("date", e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all font-medium"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Time</label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="time"
                    value={formData.time}
                    onChange={(e) => handleChange("time", e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all font-medium"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <label className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors">
                <input
                  type="checkbox"
                  checked={formData.isRecurring}
                  onChange={(e) => handleChange("isRecurring", e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-bold text-slate-700">Recurring Weekly</span>
              </label>

              {formData.isRecurring && (
                <div className="flex justify-between items-center gap-1">
                  {WEEKDAYS.map((day) => (
                    <button
                      key={day.value}
                      type="button"
                      onClick={() => toggleDay(day.value)}
                      className={`w-10 h-10 rounded-full text-xs font-bold transition-all border ${
                        formData.daysOfWeek.includes(day.value)
                          ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                          : "bg-white text-slate-500 border-slate-200 hover:border-blue-300"
                      }`}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <h3 className="font-bold text-slate-900 flex items-center gap-2 px-1"><Car className="w-4 h-4 text-blue-600" /> Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Seats</label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="number"
                    value={formData.seats}
                    onChange={(e) => handleChange("seats", e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all font-medium"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Cost per seat ($)</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="number"
                    value={formData.cost}
                    onChange={(e) => handleChange("cost", e.target.value)}
                    placeholder="0.00"
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all font-medium"
                  />
                </div>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Vehicle Info (Optional)</label>
              <input
                type="text"
                value={formData.vehicle}
                onChange={(e) => handleChange("vehicle", e.target.value)}
                placeholder="Model, Color, License Plate..."
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all font-medium"
              />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-slate-900 flex items-center gap-2 px-1"><Mail className="w-4 h-4 text-blue-600" /> Contact Info</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="email"
                value={formData.contactEmail}
                readOnly
                className="w-full pl-10 pr-4 py-3 bg-slate-100 border border-slate-200 rounded-xl text-slate-500 cursor-not-allowed"
              />
            </div>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={formData.contactPhone}
                onChange={(e) => handleChange("contactPhone", e.target.value)}
                placeholder="(XXX) XXX-XXXX"
                className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all font-medium"
              />
            </div>
          </div>
        </div>
        {errors.submit && <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-red-700 text-sm font-medium">{errors.submit}</div>}
      </form>
    </IndividualEditPageLayout>
  );
}
